import type { InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import * as _history from 'history';

import { initialState } from './rootReducer';
import configureStore from './store/configureStore';

const LOCATION_PREFIX = /^\/location\/([^/]+)(?:\/|$)/;

export const extractLocationIdFromPath = (pathname: string): string | null => {
  if (!pathname) {
    return null;
  }
  const match = LOCATION_PREFIX.exec(pathname);
  return match ? match[1] : null;
};

const normalizePath = (path: string): string => {
  if (!path) {
    return '/';
  }
  const trimmed = path.trim();
  if (!trimmed) {
    return '/';
  }
  if (trimmed === '/') {
    return '/';
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

export const buildPathForLocation = (
  basePath: string,
  locationId?: string | null
): string => {
  const normalizedBase = normalizePath(basePath);
  if (!locationId) {
    return normalizedBase;
  }
  return `/location/${locationId}${normalizedBase}`;
};

const detectBasename = (): string | undefined => {
  const locationId = extractLocationIdFromPath(window.location.pathname);
  if (!locationId) {
    return undefined;
  }
  return `/location/${locationId}`;
};

export const getActiveLocationId = (): string | null => {
  return extractLocationIdFromPath(window.location.pathname);
};

export const injectLocationQueryParam = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const method = (config.method || 'get').toUpperCase();
  if (method !== 'GET') {
    return config;
  }

  const locationId = getActiveLocationId();
  if (!locationId) {
    return config;
  }

  const url = config.url || '';
  if (/^https?:\/\//i.test(url)) {
    const currentOrigin = window.location.origin;
    if (!url.startsWith(currentOrigin)) {
      return config;
    }
  }

  if (
    !config.params ||
    (config.params as Record<string, unknown>).location === undefined
  ) {
    config.params = { ...config.params, location: locationId };
  }
  return config;
};

axios.interceptors.request.use(injectLocationQueryParam);

const initialBasename = detectBasename();

const history = initialBasename
  ? _history.createBrowserHistory({ basename: initialBasename })
  : _history.createBrowserHistory();
const store = configureStore(initialState);

export { store, history };
